import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Pause, PauseCircle, Play } from 'lucide-react';
import { z } from 'zod';
import { Form } from '@/components/ui/form';
import { formatDisplayDateTime } from '@/utils/date';
import { PCMP_HOLD_ACTION_COPY } from '../../constants/pcmpHold.constants';
import { PCMP_MKT_COMMENT_MAX } from '../../constants/pcmpProposal.constants';
import { useHoldPcmpProposal, type PcmpHoldAction } from '../../hooks/useHoldPcmpProposal';
import { PcmpCommentField } from '../PcmpMarketingActions/PcmpCommentField';
import { PcmpEditDialog } from '../PcmpProposalEdit/PcmpEditDialog';

const textFormSchema = (requiredMessage: string) =>
  z.object({
    text: z.string().trim().min(1, requiredMessage).max(PCMP_MKT_COMMENT_MAX),
  });

type TextFormValues = z.infer<ReturnType<typeof textFormSchema>>;

/** The open hold as a queue row or the details give it. */
export interface PcmpHoldSummary {
  stageLabel: string | null;
  reason: string | null;
  heldAt: string | null;
}

export interface PcmpHoldTarget {
  proposalId: number;
  refNo: string;
  action: PcmpHoldAction;
  /** Where a hold would park it, when known (details only). */
  holdStageLabel?: string | null | undefined;
  /** The hold a release lifts. */
  hold?: PcmpHoldSummary | null | undefined;
}

interface PcmpHoldDialogProps {
  target: PcmpHoldTarget;
  onClose: () => void;
}

/**
 * Legacy ProposalHold / ProposalRelease for one proposal. A hold needs a
 * reason and a release a comment; neither changes its statuses.
 */
export function PcmpHoldDialog({ target, onClose }: PcmpHoldDialogProps) {
  const { action, holdStageLabel, hold } = target;
  const copy = PCMP_HOLD_ACTION_COPY[action];
  const form = useForm<TextFormValues>({
    resolver: zodResolver(textFormSchema(copy.required)),
    defaultValues: { text: '' },
  });
  const { mutate, isPending, error } = useHoldPcmpProposal(target.proposalId, action, onClose);

  const onSubmit = form.handleSubmit((values) => mutate(values.text));

  return (
    <Form {...form}>
      <PcmpEditDialog
        open
        onOpenChange={(open) => !open && onClose()}
        title={copy.title}
        description={target.refNo}
        icon={action === 'HOLD' ? Pause : Play}
        isDirty={form.formState.isDirty}
        isPending={isPending}
        error={error}
        onSubmit={(event) => void onSubmit(event)}
        submitLabel={copy.submit}
        pendingLabel={copy.pending}
      >
        {action === 'HOLD' ? (
          <p className="text-sm text-muted-foreground">
            Pauses the proposal{holdStageLabel ? ` at ${holdStageLabel}` : ' at its current stage'}.
            Its status stays as it is; it moves to the On Hold tab until someone releases it.
          </p>
        ) : hold ? (
          <div className="flex gap-2 rounded-lg border bg-muted/40 px-4 py-3 text-sm">
            <PauseCircle className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
            <div className="flex min-w-0 flex-col gap-0.5">
              <span className="font-medium">
                On hold{hold.stageLabel ? ` at ${hold.stageLabel}` : ''}
              </span>
              {hold.reason ? <span className="whitespace-pre-line">{hold.reason}</span> : null}
              {hold.heldAt ? (
                <span className="text-xs text-muted-foreground">
                  Held {formatDisplayDateTime(hold.heldAt)}
                </span>
              ) : null}
            </div>
          </div>
        ) : null}
        <PcmpCommentField
          control={form.control}
          name="text"
          label={action === 'HOLD' ? 'Reason' : 'Comment'}
          required
          placeholder={action === 'HOLD' ? 'Why it is put on hold' : 'Why the hold is lifted'}
          description="Recorded on the proposal's timeline."
        />
      </PcmpEditDialog>
    </Form>
  );
}
